'use client';

import { getCardsByUser } from '@api/card-api';
import CardView from '@components/Card/CardView';
import { ExistingUserHandler } from '@components/Onboarding/existing-user-handler';
import OnboardingLayout, {
  BackButton,
  ForwardButton,
  MainSection,
  MainSectionContents,
  MainSectionHeader,
  NavigationButtonsSection,
  NavigationSection,
  SideSection,
} from '@components/OnboardingLayout/OnboardingLayout';
import { WithAnonymousUser } from '@components/WithUser/WithUser';
import { useUser } from '@context/user-context';
import { useEditBlinqCard } from '@hooks/card/use-edit-card';
import useAnonymousUser from '@hooks/use-anonymous-user';
import { useCreateUserDoc } from '@hooks/use-create-user-doc';
import useMedia from '@hooks/use-media';
import useOnce from '@hooks/use-once';
import { useUpdateUserDoc } from '@hooks/use-update-user-doc';
import { isIdentityBelongToAnonUser } from '@modules/auth-helpers';
import {
  createExampleCard,
  createExampleCardFromCache,
  generateFieldID,
  getDefaultLabel,
} from '@modules/card-helpers';
import type { FirebaseUser } from '@modules/firebase';
import { logger } from '@modules/logger';
import { getCachedPrefilledCardData } from '@modules/referral-helpers';
import { createAnonymousUser } from '@modules/user-helpers';
import { isEmail } from '@modules/validators';
import { Stack, TextField } from '@ui-lib';
import produce from 'immer';
import { motion } from 'motion/react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import type { AnonymousUser, Card, User } from 'types';

import styles from './personal-details.module.scss';

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
};

const PersonalDetailsPage: NextPage = () => {
  const { user, identity, loading: userIsLoading } = useUser();
  const { query, isReady: urlQueryIsReady } = useRouter();
  const anonymousUser = useAnonymousUser();
  const { editBlinqCard } = useEditBlinqCard();
  const { createUserDoc } = useCreateUserDoc();
  const { updateUserDoc } = useUpdateUserDoc();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    setFocus,
  } = useForm<FormValues>({
    mode: 'onBlur',
  });

  const router = useRouter();
  const { firstName, lastName, email } = watch();
  const [onboardingCard, setOnboardingCard] = useState<Card>();
  const [showSignOutMessage, setShowSignOutMessage] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const screenSizeCategory = useMedia<'small' | 'large'>(
    [`(min-width: ${701}px)`],
    ['large'],
    'small',
  );
  const [hideEmail, setHideEmail] = useState(false);

  // We store this in state because we don't want it to be affected if the user selects to create a new account. If we
  // directly used identity.email in the UI then when they are logged out the email would go blank.
  const [existingUserEmailAddress, setExistingUserEmailAddress] = useState('');

  useEffect(() => {
    router.prefetch('/onboarding/company-details');
    router.prefetch('/onboarding/customize-card');
  }, [router]);

  useEffect(() => {
    setFocus('firstName');
  }, [setFocus]);

  useOnce(
    (preventSecondRun) => {
      if (email) {
        preventSecondRun();
      }
      if (screenSizeCategory === 'small') {
        setHideEmail(true);
      }
    },
    [email],
  );

  useOnce(
    (preventSecondRun) => {
      if (userIsLoading || !urlQueryIsReady) return;

      async function init(user: User | undefined, firebaseUser: FirebaseUser | undefined) {
        const emailHint = query.login_hint ? String(query.login_hint) : '';

        if (firebaseUser && user?.isAnonymous === false && query.source !== 'onboarding') {
          setShowSignOutMessage(true);
          setExistingUserEmailAddress(user?.signInEmailAddress || '');
          return;
        }

        if (
          typeof window !== 'undefined' &&
          window.location.search.includes('source=onboarding') === false
        ) {
          const url = new URL(window.location.href);
          url.searchParams.set('source', 'onboarding');
          router.replace(url, url, { shallow: true });
        }

        ReactDOM.unstable_batchedUpdates(async () => {
          if (user) {
            const cards = await getCardsByUser(user.userId);
            const fetchedOnboardingCard = cards[0];
            if (fetchedOnboardingCard) {
              setOnboardingCard(fetchedOnboardingCard);
              setValue('firstName', fetchedOnboardingCard.firstName || '');
              setValue('lastName', fetchedOnboardingCard.lastName || '');

              // We don't want to show the email field if the user has logged in using an Apple private relay email address
              // as it's just a random string of characters that no one would want to display on their card.
              if (firebaseUser?.email?.includes('@privaterelay.appleid.com') === false) {
                setValue('email', firebaseUser.email);
              } else {
                setValue('email', fetchedOnboardingCard?.emails?.[0]?.value || emailHint);
              }
            } else {
              // If the user exists due to creating an accessory, there will be a user entity but no cards yet for the user
              // Create an empty onboarding card for them
              const defaultCardEmail = emailHint || '';
              setValue('email', defaultCardEmail);

              setOnboardingCard(
                createExampleCard({
                  emails: defaultCardEmail
                    ? [
                        {
                          id: generateFieldID(),
                          label: getDefaultLabel('email'),
                          value: defaultCardEmail,
                          isPreset: false,
                        },
                      ]
                    : [],
                }),
              );
            }
          } else if (isIdentityBelongToAnonUser(firebaseUser) === false) {
            const prefilledDataFromCardPage = getCachedPrefilledCardData();
            const displayName = firebaseUser?.displayName || '';
            // If the displayName is present, split on the first instance of space
            const [firstName, lastName] = displayName.split(/\s(.*)/);
            const email = firebaseUser?.email || emailHint;

            const card = createExampleCardFromCache(prefilledDataFromCardPage, {
              firstName,
              lastName,
              email,
            });

            setValue('firstName', card.firstName ?? '');
            setValue('lastName', card.lastName ?? '');
            setValue('email', card.emails?.map((e) => e.value).find(Boolean) ?? email);
            setOnboardingCard(card);
          } else {
            const prefilledDataFromCardPage = getCachedPrefilledCardData();
            const defaultCardEmail = emailHint || prefilledDataFromCardPage?.email || '';
            const defaultCard: Partial<Card> = {
              emails: defaultCardEmail
                ? [
                    {
                      id: generateFieldID(),
                      label: getDefaultLabel('email'),
                      value: defaultCardEmail,
                      isPreset: false,
                    },
                  ]
                : [],
              phoneNumbers: prefilledDataFromCardPage?.phoneNumber
                ? [
                    {
                      id: generateFieldID(),
                      label: getDefaultLabel('phone'),
                      value: prefilledDataFromCardPage?.phoneNumber,
                      isPreset: false,
                    },
                  ]
                : [],
              orgName: prefilledDataFromCardPage?.orgName,
              firstName: prefilledDataFromCardPage?.firstName,
              lastName: prefilledDataFromCardPage?.lastName,
              jobTitle: prefilledDataFromCardPage?.jobTitle,
            };

            setValue('firstName', prefilledDataFromCardPage?.firstName || '');
            setValue('lastName', prefilledDataFromCardPage?.lastName || '');
            setValue('email', defaultCardEmail);
            setOnboardingCard(createExampleCard(defaultCard));
          }
        });
      }
      preventSecondRun();
      init(user, identity);
    },
    [user, userIsLoading, identity],
  );

  const handleSave = useCallback(async () => {
    if (!onboardingCard) {
      return;
    }

    setIsSaving(true);
    try {
      if (anonymousUser?.uid && !user?.userId) {
        const newUser: AnonymousUser = createAnonymousUser(anonymousUser, {
          firstName,
          lastName,
          userId: anonymousUser.uid,
          isAnonymous: isIdentityBelongToAnonUser(anonymousUser),
          cardIds: [onboardingCard.cardId],
        });

        const card = {
          ...onboardingCard,
          userId: anonymousUser.uid,
        };

        await Promise.all([createUserDoc(newUser), editBlinqCard({ card, user: newUser })]);
      } else if (user?.userId && onboardingCard) {
        // The user already has user details and a card, i.e. they have completed this step but revisited it.
        const updatedUser = {
          ...user,
          firstName,
          lastName,
        };

        const card = {
          ...onboardingCard,
          userId: user.userId,
        };

        await Promise.all([updateUserDoc(updatedUser), editBlinqCard({ card, user: updatedUser })]);
      }
      // Redirect
      if (screenSizeCategory === 'small') {
        await router.push(`/onboarding/company-details?source=onboarding`);
      } else {
        await router.push(`/onboarding/customize-card?source=onboarding`, undefined, {
          shallow: true,
        });
      }
    } catch (e) {
      logger.error('Error saving onboarding card');
    }
    setIsSaving(false);
  }, [
    onboardingCard,
    anonymousUser,
    user,
    screenSizeCategory,
    firstName,
    lastName,
    editBlinqCard,
    router,
    updateUserDoc,
    createUserDoc,
  ]);

  async function handleCancel() {
    router.back();
  }

  useEffect(() => {
    setOnboardingCard((currentOnboardingCard) => {
      if (!currentOnboardingCard) {
        return;
      }
      return { ...currentOnboardingCard, firstName };
    });
  }, [firstName]);

  useEffect(() => {
    setOnboardingCard((currentOnboardingCard) => {
      if (!currentOnboardingCard) {
        return;
      }
      return { ...currentOnboardingCard, lastName };
    });
  }, [lastName]);

  useEffect(() => {
    setOnboardingCard(
      produce((onboardingCardDraft) => {
        if (!onboardingCardDraft) {
          return;
        }

        if (email === '') {
          onboardingCardDraft.emails = [];
          return;
        }

        if (!onboardingCardDraft.emails || onboardingCardDraft.emails.length === 0) {
          onboardingCardDraft.emails = [
            {
              id: generateFieldID(),
              label: getDefaultLabel('email'),
              value: email,
              isPreset: false,
            },
          ];
        } else {
          onboardingCardDraft.emails[0].value = email;
        }
      }),
    );
  }, [email]);

  return (
    <OnboardingLayout title="Sign up" cardColor={onboardingCard?.color}>
      <>
        {showSignOutMessage && (
          <ExistingUserHandler
            existingUserEmailAddress={existingUserEmailAddress}
          ></ExistingUserHandler>
        )}
        {!showSignOutMessage && (
          <>
            <SideSection theme={onboardingCard?.firstName ? 'light' : 'dark'}>
              <div className={styles['side-section-content']}>
                {!onboardingCard?.firstName ? (
                  <>
                    <h2 className={styles['intro-message-header']}>Let&apos;s go!</h2>
                    <p className={styles['intro-message']}>
                      Create your digital business card in under 2 minutes and take your networking
                      to the next level
                    </p>
                  </>
                ) : (
                  <CardView card={onboardingCard} mode="standalone" />
                )}
              </div>
            </SideSection>
            <MainSection>
              <MainSectionContents>
                <NavigationSection
                  currentStep="personal-details"
                  flow="card"
                  onNavigate={handleSubmit(async () => handleSave())}
                />
                <div className={styles['inputs-section']}>
                  <MainSectionHeader
                    title={'Create your free Blinq card in 2 minutes'}
                    explainer={'What name would you like to display on your business card?'}
                  />

                  <form
                    className={styles['personal-details-form']}
                    onSubmit={handleSubmit(async () => handleSave())}
                  >
                    <div className={styles['inputs']}>
                      <motion.label
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={styles['name-input']}
                      >
                        <Stack gap="medium">
                          <TextField
                            {...register('firstName', { required: true })}
                            label="First name"
                            value={firstName || ''}
                            autoComplete="given-name"
                            message={errors.firstName ? 'First name is required' : undefined}
                            tone={errors.firstName ? 'critical' : 'neutral'}
                          />
                          <TextField
                            {...register('lastName')}
                            label="Last name"
                            value={lastName || ''}
                            autoComplete="family-name"
                          />
                          <div className={styles['email-input']} data-hide={hideEmail}>
                            <TextField
                              {...register('email', {
                                required: !hideEmail,
                                validate: hideEmail ? undefined : isEmail,
                              })}
                              label="Email"
                              value={email || ''}
                              autoComplete="email"
                              message={errors.email ? "This email isn't valid" : undefined}
                              tone={errors.email ? 'critical' : 'neutral'}
                            />
                          </div>
                        </Stack>
                      </motion.label>
                    </div>

                    <NavigationButtonsSection>
                      <BackButton onClick={handleCancel}>Cancel</BackButton>
                      <ForwardButton loading={isSaving}>Next</ForwardButton>
                    </NavigationButtonsSection>
                  </form>
                </div>
              </MainSectionContents>
            </MainSection>
          </>
        )}
      </>
    </OnboardingLayout>
  );
};

export default WithAnonymousUser(PersonalDetailsPage);
